import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';

import moment from 'moment-timezone';

import { TranslatePipe } from '@recruitee/i18n';
import { RtStatefulComponent, SelectComponent, SelectOption } from '@recruitee/user-interface';
import { objectValues } from '@recruitee/utils';

import { Destroyable, takeUntilDestroyed } from '../../../common';
import { DynamicDateDropdownsInputConfig } from '../../types';
import { DynamicFormInput } from '../dynamic-form/dynamic-form-input/dynamic-form-input';

export class DateDropdownsInput extends DynamicFormInput<DynamicDateDropdownsInputConfig> {}

type State = {
  yearOptions: SelectOption[];
  monthOptions: SelectOption[];
  dayOptions: SelectOption[];
};

type DateObject = {
  year: number | null;
  month: number | null;
  day: number | null;
};

@Component({
  selector: 'rti-date-dropdowns-input',
  templateUrl: './date-dropdowns-input.component.html',
  styleUrls: ['./date-dropdowns-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, SelectComponent, TranslatePipe],
})
@Destroyable()
export class DateDropdownsInputComponent extends RtStatefulComponent<State> {
  @Input() public input: DateDropdownsInput;

  public form: UntypedFormGroup;
  private yearsLimit: number = 100;

  constructor(
    private formBuilder: UntypedFormBuilder,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.form = this.formBuilder.group({
      year: [null],
      month: [null],
      day: [null],
    });
    this.createState({
      yearOptions: this.getYearOptions(),
      monthOptions: this.getMonthOptions(),
      dayOptions: [],
    });
  }

  public ngOnInit(): void {
    const year = moment(this.input.config.value).year();
    const month = moment(this.input.config.value).month() + 1;
    const day = moment(this.input.config.value).date();

    this.setState({
      ...this.state,
      dayOptions: this.getDayOptions(year, month),
    });

    this.input.control.addValidators(this.dateValidator());

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(values => this.onFormUpdate(values));

    this.form.patchValue({
      year: `${year}`,
      month: this.getPadded(month, false),
      day: this.getPadded(day, false),
    });
  }

  public checkValueIsNull(values: DateObject) {
    if (!this.state.dayOptions.find(e => e.value === values.day)) {
      this.form.get('day')?.patchValue('', { emitEvent: false });
    }
  }

  private onFormUpdate(values: DateObject): void {
    this.setState({ ...this.state, dayOptions: this.getDayOptions(values.year, values.month) });
    this.checkValueIsNull(values);
    this.updateParentControl(values);
    this.markControlAsTouched();
    this.markControlAsDirty();
  }

  private updateParentControl(values: DateObject): void {
    let value: string | null;

    if (objectValues(values).every((item: number | null) => isNaN(item!) || String(item) === '')) {
      value = null;
    } else {
      value = `${values.year}-${values.month}-${values.day}`;
    }
    this.input.control.patchValue(value);
  }

  private markControlAsDirty() {
    if (!this.input.control.dirty) {
      this.input.control.markAsDirty();
    }
  }

  private markControlAsTouched() {
    if (!this.input.control.touched) {
      this.input.control.markAsTouched();
    }
  }

  private getYearOptions(): SelectOption[] {
    const options = [];
    const currentYear = moment().year();
    for (let year = currentYear; year >= currentYear - this.yearsLimit; year--) {
      options.push({
        label: `${year}`,
        value: `${year}`,
      });
    }
    return options;
  }

  private getMonthOptions(): SelectOption[] {
    return moment
      .localeData()
      .months()
      .map((m, index) => ({ label: m, value: this.getPadded(index) }));
  }

  private getDayOptions(year: number | null, month: number | null): SelectOption[] {
    if (year && month) {
      const days = moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();
      const result = [];
      for (let d = 0; d < days; d++) {
        result.push({
          label: this.getPadded(d),
          value: this.getPadded(d),
        });
      }
      return result;
    } else {
      return [];
    }
  }

  private getPadded(x: number, isIndex: boolean = true): string {
    let m = `${x + (isIndex ? 1 : 0)}`;
    if (m.length < 2) {
      m = `0${m}`;
    }
    return m;
  }

  private checkDateFieldsAreNaNOrEmpty(date: string): boolean {
    return String(date)
      .split('-')
      .every(item => isNaN(+item) || item === '');
  }

  private dateValidator(): ValidatorFn {
    return (formControl: FormControl): { isDateInvalid: boolean } | null =>
      this.dateValidatorCondition(formControl.value)
        ? moment(formControl.value, 'YYYY-MM-DD', true).isValid()
          ? null
          : { isDateInvalid: true }
        : null;
  }

  private dateValidatorCondition(date: string): boolean {
    return this.input.config.required ? true : !this.checkDateFieldsAreNaNOrEmpty(date);
  }
}
